import React, { useContext, useState } from 'react';
import LazyTable from 'app/component/lazy-table';
import { formatBigDecimalIntoString } from 'app/shared/util/number-utils';
import { ToastContext } from 'app/context/ToastContext';
import { generateSheets } from 'app/api/druzstvoAnniversarySheetApi';
import axios from 'axios';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { formatDateFromInstant } from 'app/shared/util/date-utils';
import { Button } from 'primereact/button';

const DruzstvoAppreciationTable: React.FC = () => {
  const { showSuccess, showError } = useContext(ToastContext);
  const [loading, setLoading] = useState(false);
  const [statusMessages, setStatusMessages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState(null);

  const onDownload = async (data: any) => {
    const response = await axios.get(`/api/druzstvo-anniversary-sheets/download/${data.id}`, {
      responseType: 'blob', // Important for binary data
    });

    // Create a URL for the blob
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', data.year + '.zip'); // Set the original file name
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const onLastElementCame = taskId => {
    setIsProcessing(false);
    showSuccess('Úspech', 'Výročné listy boli úspešne vygenerované');
  };

  const loadOtherLogs = taskId => {
    axios
      .get(`/api/druzstvo-anniversary-sheets/status/${taskId}`)
      .then(response => {
        if (response.data) {
          setStatusMessages(prev => [...prev, ...response.data]);

          if (response.data.length > 0) {
            const lastElement = response.data[response.data.length - 1];

            if (lastElement?.last) {
              onLastElementCame(taskId);
            } else {
              setTimeout(() => {
                loadOtherLogs(taskId);
              }, 2000);
            }
          } else {
            const lastElement = statusMessages[statusMessages.length - 1];

            if (lastElement?.last) {
              onLastElementCame(taskId);
            }

            setTimeout(() => {
              loadOtherLogs(taskId);
            }, 5000);
          }
        }
      })
      .catch(e => {
        console.error(e);
        setIsProcessing(false);
      });
  };

  const onGenerate = (data: any) => {
    setLoading(true);

    generateSheets(data.id)
      .then(r => {
        setCurrentTaskId(r.data.taskId);
        setShowDialog(true);

        setTimeout(() => {
          loadOtherLogs(r.data.taskId);
        }, 1000);
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const onDownloadProtocols = taskId => {
    setIsProcessing(true);

    axios
      .get(`/api/druzstvo-anniversary-sheets/status/download/${taskId}`, {
        responseType: 'blob',
      })
      .then(response => {
        const blob = new Blob([response.data], { type: 'text/plain' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = 'protocols.txt';
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', 'Neočakávaná chyba pri sťahovaní súboru');
      })
      .finally(() => setIsProcessing(false));
  };

  const dialogFooter = (
    <React.Fragment>
      <Button
        style={{ backgroundColor: '#e31818' }}
        disabled={isProcessing}
        label="Stiahnuť protokoly"
        onClick={() => onDownloadProtocols(currentTaskId)}
      />
    </React.Fragment>
  );

  const formatLevel = level => {
    return <span style={{ color: level === 'ERROR' ? 'red' : level === 'WARN' ? 'orange' : 'blue' }}>{level}</span>;
  };

  return (
    <>
      {LazyTable({
        initialSortField: 'year',
        initialSortDirection: 'desc',
        createUrl: '/create-druzstvo-appreciation',
        labelForCreateEntity: 'Vytvoriť zhodnotenie',
        serverUrl: '/api/druzstvo-appreciations',
        editActionAllowed: true,
        searchAllowed: false,
        globalFilterFields: ['businessName', 'firstName', 'lastName', 'email'],
        columns: [
          { field: 'year', header: 'Rok', sortable: true },
          { field: 'appreciation', header: 'Zhodnotenie za rok (%)', sortable: true, formatter: v => formatBigDecimalIntoString(v) + ' %' },
        ],
        additionalActions: [
          {
            icon: 'pi pi-cog',
            tooltip: 'Generovať výročné listy',
            onClick: onGenerate,
          },
          {
            icon: 'pi pi-download',
            tooltip: 'Stiahnuť výročné listy',
            onClick: onDownload,
          },
        ],
      })}
      {showDialog && (
        <Dialog
          footer={dialogFooter}
          visible={showDialog}
          style={{ width: '90%' }}
          header="Protokoly importu"
          onHide={() => setShowDialog(false)}
        >
          <div style={{ width: '100%' }}>
            <DataTable
              stripedRows
              emptyMessage="Žiadne protokoly"
              size="small"
              lazy
              value={statusMessages}
              responsiveLayout="scroll"
              className="p-mt-4"
            >
              <Column field="level" header="Level" body={v => formatLevel(v.level)} />
              <Column field="message" header="Správa" />
              <Column field="timestamp" header="Čas" body={v => formatDateFromInstant(v.timestamp)} />
            </DataTable>
          </div>
        </Dialog>
      )}
    </>
  );
};

export default DruzstvoAppreciationTable;
